import { useEffect, useState, type Dispatch, type SetStateAction } from 'react'

/**
 * Session-scoped memory for in-page UI state (PD-1206).
 *
 * Route elements unmount on every nav-tab switch, so plain `useState` inside a
 * page is discarded the moment the user leaves the tab. This module-level map
 * lives above that boundary and gives a section back the filters, view mode,
 * selection and expand/collapse state it had when the user left.
 *
 * Deliberately in-memory: it dies with the page, which is exactly the ruled
 * durability (survive a tab switch, not a reload). Nothing is serialized, so no
 * patient, thread or conversation identifier ever reaches disk.
 */
const store = new Map<string, unknown>()

const remembered = <T>(key: string, initial: T): T =>
  store.has(key) ? (store.get(key) as T) : initial

/**
 * Drop-in `useState` whose value survives unmount for the rest of the session.
 * Same tuple and the same setter semantics, updater functions included.
 *
 * Keys are global — give each call site its own (`'home:messages:filter'`).
 * A call site may also vary its key over the lifetime of one mounted component
 * (the Messages drawer keys by patient id); the value follows the key.
 */
export function useSessionState<T>(key: string, initial: T): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => remembered(key, initial))

  // Re-read the store when the key changes under a component that stays mounted.
  // The `useState` initializer runs once per mount, so without this the value
  // belonging to the OLD key would survive the switch and the effect below would
  // then file it under the NEW key. That is not hypothetical: the patient
  // chart's Messages drawer keys by patient id, and `/patients/:patientId` is a
  // param change rather than a remount, so patient A's open thread would be
  // written into patient B's slot and resurface as B's thread.
  //
  // React's documented "adjusting state when a prop changes" pattern: the
  // setState during render is discarded and re-rendered before commit, so the
  // effect only ever sees a matching key/value pair.
  const [renderedKey, setRenderedKey] = useState(key)
  if (renderedKey !== key) {
    setRenderedKey(key)
    setValue(() => remembered(key, initial))
  }

  // Written after commit rather than inside the setter, so the store only ever
  // holds a value the UI actually rendered — and `setValue` stays React's own
  // stable setter, safe to use as an effect dependency at the call sites.
  useEffect(() => {
    store.set(key, value)
  }, [key, value])

  return [value, setValue]
}

/**
 * Drops remembered values. With no arguments, everything — called on logout so a
 * second clinician signing in on the same tab without a reload does not inherit
 * the first one's selected patient, thread or conversation.
 *
 * `prefix` narrows it to one section's keys, which is how clicking a nav tab you
 * are already inside resets that section (PD-1206). `keep` spares specific keys,
 * so a caller can bound its own keyspace without losing the entry it still uses.
 */
export function clearSessionState(prefix?: string, keep: readonly string[] = []): void {
  if (prefix === undefined) {
    store.clear()
    return
  }
  for (const key of [...store.keys()]) {
    if (key.startsWith(prefix) && !keep.includes(key)) store.delete(key)
  }
}
